import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, Receipt, Wallet, TrendingUp, Shield, Plus, X, Download, DollarSign, 
  ShieldCheck, AlertTriangle, CheckCircle, Paperclip, Trash2, Search, Calendar, 
  Filter, ArrowRight, Eye, Printer, FileSpreadsheet, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ESSPayTax = ({ mode = 'pay_and_tax' }) => {
  // mode:
  //   'pay_and_tax'  → Payslips + Tax Summary tabs only (default)
  //   'reimbursement' → renders ONLY the Reimbursement section (no tab strip)
  //   'loan'          → renders ONLY the Loans section (no tab strip)
  const initialTab = mode === 'reimbursement' ? 'reimbursement'
                    : mode === 'loan' ? 'loan'
                    : 'payslips';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [payslips, setPayslips] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  
  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Details Modal states
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  // Stage 73.20cm — FY selector so the YTD card is reachable for past
  // financial years (employees often only have data in a non-current FY
  // when joining late or after a payroll-system import).
  const _currentFy = () => {
    const d = new Date();
    const y = d.getFullYear();
    // India FY: Apr → Mar; Jan-Mar belongs to the previous start year.
    const startY = d.getMonth() >= 3 ? y : y - 1;
    return `${startY}-${String((startY + 1) % 100).padStart(2, '0')}`;
  };
  const [selectedFy, setSelectedFy] = useState(_currentFy());
  // Stage 73.20dk (Phase 1) — Pull the dynamic FY list from the Tax Slab
  // Master so year-wise control stays consistent across ESS + Admin.
  const [dynamicFys, setDynamicFys] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/tax-slab-master/meta/financial-years`, { withCredentials: true })
      .then((r) => { if (!cancelled) setDynamicFys(r.data?.financial_years || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const fyOptions = useMemo(() => {
    if (dynamicFys && dynamicFys.length) return dynamicFys;
    // Fallback while the API is loading — show current + 4 prior FYs.
    const opts = [];
    const cy = _currentFy();
    const [curStart] = cy.split('-').map(Number);
    for (let i = 0; i < 5; i += 1) {
      const s = curStart - i;
      opts.push(`${s}-${String((s + 1) % 100).padStart(2, '0')}`);
    }
    return opts;
  }, [dynamicFys]);
  const [reimbPolicy, setReimbPolicy] = useState({ has_policy: false, designation_name: null, items: [] });
  const [loanPolicy, setLoanPolicy] = useState({ has_policy: false, designation_name: null, items: [] });
  const [showReimbModal, setShowReimbModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [submittingReimb, setSubmittingReimb] = useState(false);
  const [reimbForm, setReimbForm] = useState({ category: 'travel', amount: '', description: '', bill_date: new Date().toISOString().slice(0, 10), bill_number: '' });
  // Stage 73.20dd — Attached bills for the New Claim modal (image/PDF, ≤10MB).
  const [reimbFiles, setReimbFiles] = useState([]);
  const [loanForm, setLoanForm] = useState({ loan_type: 'salary_advance', amount: 0, tenure_months: 12, reason: '' });
  // Stage 73.20co (Phase A) — Tax sheet release + YTD/projection card.
  const [taxSheetInfo, setTaxSheetInfo] = useState(null);
  const [taxSheetLoading, setTaxSheetLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      setTaxSheetLoading(true);
      const [ps, rb, lr, ts, pol, lpol, ledg, tsi] = await Promise.all([
        axios.get(`${API_URL}/ess/payslips`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/reimbursements`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/loan-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/tax-summary?financial_year=${selectedFy}`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/reimbursement-policy`, { withCredentials: true }).catch(() => ({ data: { has_policy: false, items: [] } })),
        axios.get(`${API_URL}/ess/loan-policy`, { withCredentials: true }).catch(() => ({ data: { has_policy: false, items: [] } })),
        axios.get(`${API_URL}/advance-ledger/me/summary`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/ess/tax-sheet/summary?fy=${selectedFy}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPayslips(ps.data);
      setReimbursements(rb.data);
      setLoanRequests(lr.data);
      setTaxSummary(ts.data);
      setTaxSheetInfo(tsi.data);
      setReimbPolicy(pol.data || { has_policy: false, items: [] });
      setLoanPolicy(lpol.data || { has_policy: false, items: [] });
      setLedgerSummary(ledg ? ledg.data : null);
      if (pol.data?.items?.length) {
        setReimbForm((f) => ({ ...f, category: pol.data.items[0].category }));
      }
      if (lpol.data?.items?.length) {
        const it0 = lpol.data.items[0];
        setLoanForm((f) => ({
          ...f,
          loan_type: it0.loan_type,
          tenure_months: Math.min(f.tenure_months || it0.max_tenure_months || 12, it0.max_tenure_months || 12),
        }));
      }
    } catch (e) { console.error(e); }
    finally { setTaxSheetLoading(false); }
  }, [selectedFy]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const lower = tabParam.toLowerCase();
      if (lower === 'payslips' || lower === 'payslip') setActiveTab('payslips');
      else if (lower === 'tax' || lower === 'tax_summary' || lower === 'tax-summary' || lower === 'tax_documents') setActiveTab('tax');
      else if (lower === 'reimbursement' || lower === 'reimbursements') setActiveTab('reimbursement');
      else if (lower === 'loan' || lower === 'loans') setActiveTab('loan');
    }
  }, []);

  // Sync activeTab and clear form / modal state when mode changes
  useEffect(() => {
    const nextTab = mode === 'reimbursement' ? 'reimbursement'
                  : mode === 'loan'          ? 'loan'
                  : 'payslips';
    setActiveTab(nextTab);
    setShowReimbModal(false);
    setShowLoanModal(false);
    setReimbFiles([]);
    
    const initialCategory = reimbPolicy?.items?.[0]?.category || 'travel';
    setReimbForm({ category: initialCategory, amount: 0, description: '', bill_date: '', bill_number: '' });
    
    const initialLoanType = loanPolicy?.items?.[0]?.loan_type || 'salary_advance';
    const initialTenure = loanPolicy?.items?.[0]?.max_tenure_months || 12;
    setLoanForm({ loan_type: initialLoanType, amount: 0, tenure_months: initialTenure, reason: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleReimbSubmit = async (e) => {
    e.preventDefault();
    if (!reimbForm.category || !reimbForm.category.trim()) {
      toast.error('Please select a reimbursement category');
      return;
    }
    const claimAmount = parseFloat(reimbForm.amount);
    if (isNaN(claimAmount) || claimAmount <= 0) {
      toast.error('Please enter a valid claim amount greater than 0');
      return;
    }
    if (!reimbForm.bill_date || !String(reimbForm.bill_date).trim()) {
      toast.error('Please select the bill / expense date');
      return;
    }
    if (!reimbForm.description || !reimbForm.description.trim()) {
      toast.error('Please enter a description for the claim');
      return;
    }

    setSubmittingReimb(true);
    try {
      const payload = {
        category: reimbForm.category.trim(),
        amount: claimAmount,
        description: reimbForm.description.trim(),
        bill_date: reimbForm.bill_date,
        bill_number: (reimbForm.bill_number || '').trim(),
      };

      const res = await axios.post(`${API_URL}/ess/reimbursements`, payload, {
        withCredentials: true,
      });

      const claimId = res.data?.id || res.data?._id;
      if (claimId && reimbFiles.length > 0) {
        const failed = [];
        for (const f of reimbFiles) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            await axios.post(
              `${API_URL}/ess/reimbursements/${claimId}/lines/primary/attachments`,
              fd,
              { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
          } catch (err) {
            failed.push(f.name);
          }
        }
        if (failed.length) {
          toast.error(`Claim created, but these attachments failed: ${failed.join(', ')}`);
        }
      }

      toast.success('Reimbursement claim submitted successfully');
      setShowReimbModal(false);
      setReimbForm({
        category: reimbPolicy?.items?.[0]?.category || 'travel',
        amount: '',
        description: '',
        bill_date: new Date().toISOString().slice(0, 10),
        bill_number: '',
      });
      setReimbFiles([]);
      await fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : (err.message || 'Failed to submit reimbursement claim'));
      toast.error(msg);
    } finally {
      setSubmittingReimb(false);
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ess/loan-requests`, loanForm, { withCredentials: true });
      setShowLoanModal(false);
      setLoanForm({ loan_type: 'salary_advance', amount: 0, tenure_months: 12, reason: '' });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  // Stage 73.20cm — Download payslip PDF via the new ESS endpoint.
  // Uses the LOGGED-IN employee's identity (no employee_id in URL) so
  // an employee can only ever download their own payslip.
  const handlePayslipDownload = async (period) => {
    try {
      const res = await axios.get(`${API_URL}/ess/payslips/${encodeURIComponent(period)}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Blob-typed errors leave detail wrapped as a Blob — coerce back to text.
      let detail = e.response?.data?.detail;
      if (!detail && e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          detail = JSON.parse(txt).detail;
        } catch { /* ignore */ }
      }
      const msg = e.response?.status === 404
        ? `No payslip found for ${period}. Payroll may not be processed yet.`
        : e.response?.status === 403
          ? (detail || 'This payslip has not been released yet by the payroll team.')
          : (detail || 'Failed to download payslip PDF.');
      toast.error(msg);
    }
  };

  // Stage 73.20co (Phase A) — Download the Tax Computation Sheet PDF.
  // Gated on admin release; returns 403 if not released yet.
  const handleTaxSheetDownload = async () => {
    try {
      const res = await axios.get(`${API_URL}/ess/tax-sheet/pdf?fy=${selectedFy}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax_sheet_${selectedFy}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.status === 403
        ? 'Your tax sheet has not been released by the payroll team yet.'
        : (e.response?.data?.detail || 'Failed to download tax sheet.');
      alert(msg);
    }
  };

  const renderAdvanceTimeline = (l, adv) => {
    const isApproved = l.status === 'approved' || l.status === 'paid' || l.status === 'closed' || (adv && adv.approved_amount > 0);
    const isPaid = l.status === 'paid' || l.status === 'closed' || (adv && adv.finance_paid_amount > 0);
    const isClaimSubmitted = (adv && adv.claim_submitted_amount > 0) || (adv && adv.claim_approved_amount > 0);
    const isClaimApproved = (adv && adv.claim_approved_amount > 0);
    const isClosed = l.status === 'closed' || (adv && adv.closed);
    
    const steps = [
      { label: 'Requested', done: true },
      { label: 'Approved', done: isApproved },
      { label: 'Paid', done: isPaid },
      { label: 'Claim Submitted', done: isClaimSubmitted },
      { label: 'Claim Approved', done: isClaimApproved },
      { label: 'Closed', done: isClosed }
    ];

    return (
      <div className="flex items-center gap-1.5 py-3 overflow-x-auto text-[10px] sm:text-xs border-t border-slate-100 mt-3">
        {steps.map((s, idx) => (
          <React.Fragment key={idx}>
            <div className="flex items-center gap-1 shrink-0">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                s.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {s.done ? '✓' : idx + 1}
              </span>
              <span className={s.done ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <span className={`h-[2px] w-6 shrink-0 ${
                steps[idx + 1].done ? 'bg-emerald-400' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderReimbursementTimeline = (r) => {
    const approvals = r.approvals || [];
    const sheetStatus = r.status || 'pending';

    if (approvals.length > 0) {
      const steps = [
        { label: 'Submitted', done: true },
        ...approvals.map((a) => ({
          label: a.level_name || 'Approval',
          done: a.status === 'approved',
          rejected: a.status === 'rejected',
          pending: a.status === 'pending',
        })),
        { label: 'Paid', done: sheetStatus === 'paid' },
      ];
      return (
        <div className="flex items-center gap-1.5 py-3 overflow-x-auto text-[10px] sm:text-xs border-t border-slate-100 mt-3">
          {steps.map((s, idx) => (
            <React.Fragment key={idx}>
              <div className="flex items-center gap-1 shrink-0">
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                  s.rejected ? 'bg-rose-500 text-white'
                    : s.done ? 'bg-emerald-500 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}>
                  {s.rejected ? '✗' : s.done ? '✓' : idx + 1}
                </span>
                <span className={s.done ? 'text-emerald-700 font-semibold' : s.rejected ? 'text-rose-700 font-semibold' : 'text-slate-500'}>
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <span className={`h-[2px] w-6 shrink-0 ${
                  steps[idx + 1].done ? 'bg-emerald-400' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }

    const isApproved = sheetStatus === 'approved' || sheetStatus === 'paid' || sheetStatus === 'processing';
    const isPaid = sheetStatus === 'paid';
    const steps = [
      { label: 'Submitted', done: true },
      { label: 'Approved', done: isApproved },
      { label: 'Paid', done: isPaid },
    ];
    return (
      <div className="flex items-center gap-1.5 py-3 overflow-x-auto text-[10px] sm:text-xs border-t border-slate-100 mt-3">
        {steps.map((s, idx) => (
          <React.Fragment key={idx}>
            <div className="flex items-center gap-1 shrink-0">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                s.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {s.done ? '✓' : idx + 1}
              </span>
              <span className={s.done ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <span className={`h-[2px] w-6 shrink-0 ${
                steps[idx + 1].done ? 'bg-emerald-400' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const handleReimbPdfDownload = async (r) => {
    const sheetId = r.sheet_id || r.id;
    if (!sheetId) return;
    try {
      const res = await axios.get(`${API_URL}/ess/expense-claims/${sheetId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense_claim_${sheetId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const handleReimbAttachmentsDownload = async (r) => {
    const lineIds = (r.line_ids && r.line_ids.length) ? r.line_ids : (r.id ? [r.id] : []);
    if (!lineIds.length) {
      toast.info('No attachments found for this claim.');
      return;
    }
    let downloaded = 0;
    for (const claimId of lineIds) {
      try {
        const { data: attachments } = await axios.get(
          `${API_URL}/ess/reimbursements/${claimId}/attachments`,
          { withCredentials: true },
        );
        for (const att of attachments || []) {
          const res = await axios.get(
            `${API_URL}/ess/reimbursements/attachments/${att.id}/download`,
            { withCredentials: true, responseType: 'blob' },
          );
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = att.filename || `attachment_${att.id}`;
          a.click();
          URL.revokeObjectURL(url);
          downloaded += 1;
        }
      } catch (err) {
        /* skip lines without attachments */
      }
    }
    if (downloaded === 0) toast.info('No attachments found for this claim.');
    else toast.success(`Downloaded ${downloaded} attachment(s).`);
  };

  const statusColor = (s) => ({ 
    approved: 'bg-emerald-100 text-emerald-700', 
    pending: 'bg-amber-100 text-amber-700', 
    rejected: 'bg-rose-100 text-rose-700', 
    processed: 'bg-blue-100 text-blue-700',
    paid: 'bg-indigo-100 text-indigo-700',
    closed: 'bg-slate-700 text-white',
    settled: 'bg-emerald-100 text-emerald-800'
  }[s] || 'bg-slate-100 text-slate-600');

  const categoryColor = (c) => ({ 
    travel: 'bg-blue-100 text-blue-700', 
    medical: 'bg-rose-100 text-rose-700', 
    food: 'bg-amber-100 text-amber-800', 
    mobile: 'bg-purple-100 text-purple-700', 
    other: 'bg-slate-100 text-slate-700' 
  }[c] || 'bg-slate-100 text-slate-700');

  const exportAdvancesCSV = () => {
    const headers = ['Advance ID', 'Type', 'Request Date', 'Requested Amount', 'Approved Amount', 'Outstanding', 'Status', 'Purpose'];
    const rows = filteredAdvances.map(l => {
      const adv = (ledgerSummary?.per_advance || []).find(a => a.advance_id === l.id);
      return [
        l.id || 'Pending',
        l.loan_type?.replace(/_/g, ' ').toUpperCase(),
        l.created_at?.slice(0, 10) || '',
        l.amount || 0,
        adv?.approved_amount || l.amount || 0,
        adv?.current_outstanding ?? l.amount,
        l.status?.toUpperCase(),
        l.reason || ''
      ];
    });
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "my_advances.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportReimbursementsCSV = () => {
    const headers = ['Claim No', 'Expense Sheet Ref', 'Employee', 'Expense Type', 'Amount', 'Approved Amount', 'Submitted Date', 'Status', 'Payment Date'];
    const rows = filteredReimbursements.map(r => [
      r.claim_no || r.bill_number || '—',
      r.expense_sheet_ref || r.claim_no || '—',
      r.employee_name || '—',
      r.expense_type || r.category?.toUpperCase() || '—',
      r.amount || 0,
      r.approved_amount ?? '—',
      (r.submitted_date || r.created_at)?.slice(0, 10) || '',
      r.status?.toUpperCase() || '—',
      r.payment_date || '—',
    ]);
    
    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "my_reimbursements.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Stage 73.20p — Live policy match for the modal. Looks up the
  // selected category against the designation policy and computes
  // whether the typed amount is within / over the cap, plus whether
  // it qualifies for auto-approval.
  const matchedPolicyItem = useMemo(() => {
    if (!reimbPolicy?.items?.length || !reimbForm.category) return null;
    const cat = (reimbForm.category || '').toLowerCase();
    return reimbPolicy.items.find((it) => (it.category || '').toLowerCase() === cat) || null;
  }, [reimbPolicy, reimbForm.category]);

  const liveCapStatus = useMemo(() => {
    if (!matchedPolicyItem) return null;
    const amt = parseFloat(reimbForm.amount) || 0;
    const cap = parseFloat(matchedPolicyItem.limit_amount) || 0;
    const auto = matchedPolicyItem.auto_approve_below;
    const exceedsBy = Math.max(amt - cap, 0);
    const within = exceedsBy === 0;
    const autoApprove = within && auto != null && amt > 0 && amt <= parseFloat(auto);
    return {
      cap,
      unit: matchedPolicyItem.limit_unit,
      autoThreshold: auto != null ? parseFloat(auto) : null,
      amt,
      exceedsBy,
      within,
      autoApprove,
      requiresBill: matchedPolicyItem.requires_bill,
    };
  }, [matchedPolicyItem, reimbForm.amount]);

  // Stage 73.20q — Live loan policy match: principal + tenure caps.
  const matchedLoanPolicyItem = useMemo(() => {
    if (!loanPolicy?.items?.length || !loanForm.loan_type) return null;
    return loanPolicy.items.find((it) => it.loan_type === loanForm.loan_type) || null;
  }, [loanPolicy, loanForm.loan_type]);

  const liveLoanStatus = useMemo(() => {
    if (!loanPolicy.has_policy) return null;
    if (!matchedLoanPolicyItem) {
      // Loan type not in policy → not allowed, will need special approval.
      return {
        allowed: false,
        principalWithin: true,
        tenureWithin: true,
        maxPrincipal: 0,
        maxTenure: 0,
        principalExceedsBy: 0,
        tenureExceedsBy: 0,
      };
    }
    const amt = parseFloat(loanForm.amount) || 0;
    const tenure = parseInt(loanForm.tenure_months) || 0;
    const maxP = parseFloat(matchedLoanPolicyItem.max_principal) || 0;
    const maxT = parseInt(matchedLoanPolicyItem.max_tenure_months) || 0;
    const minT = parseInt(matchedLoanPolicyItem.min_tenure_months) || 1;
    const principalExceedsBy = Math.max(amt - maxP, 0);
    const tenureExceedsBy = Math.max(tenure - maxT, 0);
    const tenureUnderMin = tenure > 0 && tenure < minT;
    return {
      allowed: true,
      principalWithin: principalExceedsBy === 0,
      tenureWithin: tenureExceedsBy === 0 && !tenureUnderMin,
      tenureUnderMin,
      minTenure: minT,
      maxPrincipal: maxP,
      maxTenure: maxT,
      principalExceedsBy,
      tenureExceedsBy,
    };
  }, [loanPolicy, matchedLoanPolicyItem, loanForm.amount, loanForm.tenure_months]);

  const filteredAdvances = useMemo(() => {
    return loanRequests.filter(l => {
      const adv = (ledgerSummary?.per_advance || []).find(a => a.advance_id === l.id);
      
      const matchesSearch = () => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        if (l.id?.toLowerCase().includes(q)) return true;
        if (l.reason?.toLowerCase().includes(q)) return true;
        if (adv?.finance_ref?.toLowerCase().includes(q)) return true;
        return false;
      };

      const matchesStatus = () => {
        if (statusFilter === 'ALL') return true;
        return l.status === statusFilter;
      };

      const matchesCategory = () => {
        if (categoryFilter === 'ALL') return true;
        return l.loan_type === categoryFilter;
      };

      const matchesDate = () => {
        if (!l.created_at) return true;
        const reqDate = l.created_at.slice(0, 10);
        if (startDate && reqDate < startDate) return false;
        if (endDate && reqDate > endDate) return false;
        return true;
      };

      return matchesSearch() && matchesStatus() && matchesCategory() && matchesDate();
    });
  }, [loanRequests, ledgerSummary, searchTerm, statusFilter, categoryFilter, startDate, endDate]);

  const filteredReimbursements = useMemo(() => {
    return reimbursements.filter(r => {
      const matchesSearch = () => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        if (r.claim_no?.toLowerCase().includes(q)) return true;
        if (r.expense_sheet_ref?.toLowerCase().includes(q)) return true;
        if (r.bill_number?.toLowerCase().includes(q)) return true;
        if (r.title?.toLowerCase().includes(q)) return true;
        if (r.description?.toLowerCase().includes(q)) return true;
        if (r.expense_type?.toLowerCase().includes(q)) return true;
        return false;
      };

      const matchesStatus = () => {
        if (statusFilter === 'ALL') return true;
        if (statusFilter === 'processing') {
          return r.status === 'approved' && !r.payment_date;
        }
        return r.status === statusFilter;
      };

      const matchesCategory = () => {
        if (categoryFilter === 'ALL') return true;
        const types = (r.expense_type || r.category || '').toLowerCase();
        return types.includes(categoryFilter.toLowerCase());
      };

      const matchesDate = () => {
        const dateVal = (r.submitted_date || r.created_at || '').slice(0, 10);
        if (!dateVal) return true;
        if (startDate && dateVal < startDate) return false;
        if (endDate && dateVal > endDate) return false;
        return true;
      };

      return matchesSearch() && matchesStatus() && matchesCategory() && matchesDate();
    });
  }, [reimbursements, searchTerm, statusFilter, categoryFilter, startDate, endDate]);

  const tabs = mode === 'reimbursement' || mode === 'loan'
    ? []  // No tab strip — this page is dedicated to a single module now.
    : [
        { key: 'payslips', label: 'Payslips', icon: FileText },
        { key: 'tax', label: 'Tax Summary', icon: TrendingUp },
      ];

  const pageTitle = mode === 'reimbursement' ? 'Reimbursements'
                  : mode === 'loan'          ? 'Advance'
                  : 'Pay & Tax';

  return (
    <MainLayout>
      <div data-testid={mode === 'reimbursement' ? 'ess-reimbursements-page' : mode === 'loan' ? 'ess-loans-page' : 'ess-pay-tax-page'} className="space-y-6 max-w-5xl">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>

        {tabs.length > 0 && (
          <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 py-2 px-4 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <tab.icon className="w-4 h-4" /><span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* PAYSLIPS TAB */}
        {activeTab === 'payslips' && (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {payslips.length > 0 ? payslips.map((ps, i) => (
              <div key={ps.id || ps._id || ps.period || ps.period_name || i} data-testid={`payslip-${i}`} className="flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-50 rounded-lg"><DollarSign className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800">{ps.period_name || ps.period || 'Payslip'}</p>
                    </div>
                    <p className="text-xs text-slate-500">Gross: {ps.gross_pay?.toLocaleString()} &middot; Net: {ps.net_pay?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    data-testid={`payslip-download-${i}`}
                    onClick={() => handlePayslipDownload(ps.period || ps.period_name)}
                    disabled={!(ps.period || ps.period_name)}
                    className="flex items-center space-x-1.5 bg-[#0052FF] hover:bg-[#0040CC] disabled:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-70 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                    title={(ps.period || ps.period_name) ? `Download payslip for ${ps.period || ps.period_name}` : 'No period — cannot download'}
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            )) : <p className="text-sm text-slate-400 py-8 text-center">No payslips available yet</p>}
          </div>
        )}

        {/* TAX SUMMARY TAB */}
        {activeTab === 'tax' && (
          <div className="space-y-4">
            {/* Stage 73.20cm — FY selector. Lets the employee browse
                historical years; defaults to the current FY. */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
              <div>
                <p className="text-xs text-slate-500">Financial Year</p>
                <p className="text-sm font-medium text-slate-800">{selectedFy} (Apr → Mar)</p>
              </div>
              <select
                data-testid="ytd-fy-selector"
                value={selectedFy}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="text-sm border border-slate-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:border-[#0052FF]"
              >
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>{fy}</option>
                ))}
              </select>
            </div>

            {/* Stage 73.20co (Phase A) — Tax Sheet Release + Download card
                Stage 73.20dk (Phase 1) — 4-state visibility, FY + AY chip. */}
            <div data-testid="tax-sheet-release-card" className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Tax Computation Sheet</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    FY <span className="font-medium text-slate-700">{taxSheetInfo?.financial_year || selectedFy}</span>
                    {taxSheetInfo?.assessment_year && (
                      <> · AY <span className="font-medium text-slate-700">{taxSheetInfo.assessment_year}</span></>
                    )}
                    <> · Regime <span className="font-medium text-slate-700 capitalize">{taxSheetInfo?.regime || 'new'}</span></>
                    {taxSheetInfo?.state === 'released' && (
                      <span data-testid="tax-sheet-state-badge" className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Released</span>
                    )}
                    {taxSheetInfo?.state === 'generated_not_released' && (
                      <span data-testid="tax-sheet-state-badge" className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Awaiting release</span>
                    )}
                    {taxSheetInfo?.state === 'ctc_projection' && (
                      <span data-testid="tax-sheet-state-badge" className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Projection</span>
                    )}
                    {taxSheetInfo?.state === 'not_applicable' && (
                      <span data-testid="tax-sheet-state-badge" className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">Not applicable</span>
                    )}
                  </p>
                </div>
                <button
                  data-testid="download-tax-sheet-btn"
                  onClick={handleTaxSheetDownload}
                  disabled={taxSheetInfo?.state !== 'released'}
                  className="flex items-center space-x-1.5 bg-[#0052FF] hover:bg-[#0040CC] disabled:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-70 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  title={taxSheetInfo?.state === 'released' ? 'Download tax sheet PDF' : (taxSheetInfo?.state_label || 'Not released yet')}
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download Tax Sheet PDF</span>
                </button>
              </div>
              {taxSheetInfo?.state === 'ctc_projection' && (
                <div data-testid="tax-sheet-ctc-projection" className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3 mb-3">
                  Payroll has not been processed yet for this financial year. The figures below are <strong>projected</strong> based on your configured CTC ({taxSheetInfo?.months_remaining || 12} months × monthly gross). Actual TDS will be adjusted once payroll runs begin.
                </div>
              )}
              {taxSheetInfo?.state === 'not_applicable' && (
                <div data-testid="tax-sheet-not-applicable" className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3 mb-3">
                  No payroll has been processed for you in this financial year and no CTC is configured, so no tax computation is applicable.
                </div>
              )}
              {taxSheetInfo?.state === 'generated_not_released' && (
                <div data-testid="tax-sheet-pending" className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3 mb-3">
                  Your tax sheet has been generated but is awaiting release by the payroll team.
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div data-testid="stat-current-month-tds" className="border border-slate-100 rounded-md p-3">
                  <p className="text-xs text-slate-500">Current Month TDS</p>
                  <p className="text-base font-bold text-red-700">₹{(taxSheetInfo?.current_month_tds ?? taxSummary?.taxSheet?.currentMonthTDS ?? taxSummary?.ytd?.current_month_tds ?? 0).toLocaleString()}</p>
                </div>
                <div data-testid="stat-ytd-tds" className="border border-slate-100 rounded-md p-3">
                  <p className="text-xs text-slate-500">YTD TDS</p>
                  <p className="text-base font-bold text-slate-800">₹{(taxSheetInfo?.ytd_tds ?? taxSummary?.taxSheet?.ytdTDS ?? taxSummary?.ytd?.tds_deducted ?? 0).toLocaleString()}</p>
                </div>
                <div data-testid="stat-projected-annual-tds" className="border border-slate-100 rounded-md p-3">
                  <p className="text-xs text-slate-500">Projected Annual TDS</p>
                  <p className="text-base font-bold text-amber-700">₹{(taxSheetInfo?.projected_annual_tds ?? taxSummary?.taxSheet?.projectedAnnualTDS ?? taxSummary?.ytd?.projected_annual_tds ?? 0).toLocaleString()}</p>
                </div>
                <div data-testid="stat-closing-balance-tds" className="border border-slate-100 rounded-md p-3">
                  <p className="text-xs text-slate-500">Closing Balance TDS</p>
                  <p className={`text-base font-bold ${(taxSheetInfo?.closing_balance_tds ?? taxSummary?.taxSheet?.closingBalanceTDS ?? 0) > 0 ? 'text-red-700' : 'text-emerald-700'}`}>₹{Math.abs(taxSheetInfo?.closing_balance_tds ?? taxSummary?.taxSheet?.closingBalanceTDS ?? 0).toLocaleString()}{(taxSheetInfo?.closing_balance_tds ?? taxSummary?.taxSheet?.closingBalanceTDS ?? 0) < 0 ? ' (Refund)' : ''}</p>
                </div>
              </div>

              {/* Stage 73.20dl (Phase 2) — TDS Recovery Summary breakdown */}
              <div data-testid="tds-recovery-summary" className="mt-4 border-t border-slate-100 pt-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">TDS Recovery Summary</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Annual tax payable</span>
                    <span className="font-medium text-slate-800">₹{(taxSheetInfo?.annual_tax_payable ?? taxSummary?.taxSheet?.annualTaxPayable ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">TDS till previous month</span>
                    <span className="font-medium text-slate-800">₹{(taxSheetInfo?.tds_till_previous_month ?? taxSummary?.taxSheet?.tdsTillPreviousMonth ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Balance before current</span>
                    <span className="font-medium text-slate-800">₹{(taxSheetInfo?.balance_before_current_month ?? taxSummary?.taxSheet?.balanceBeforeCurrent ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">TDS till current month</span>
                    <span className="font-medium text-slate-800">₹{(taxSheetInfo?.tds_till_current_month ?? taxSummary?.taxSheet?.tdsTillCurrentMonth ?? taxSheetInfo?.ytd_tds ?? taxSummary?.ytd?.tds_deducted ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Months processed / remaining</span>
                    <span className="font-medium text-slate-800">
                      {taxSheetInfo?.months_processed ?? taxSheetInfo?.months_paid ?? taxSummary?.ytd?.months_processed ?? 0} / {taxSheetInfo?.months_remaining ?? taxSummary?.ytd?.months_remaining ?? (12 - (taxSheetInfo?.months_processed ?? taxSheetInfo?.months_paid ?? taxSummary?.ytd?.months_processed ?? 0))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Projection source</span>
                    <span className="font-medium text-slate-700 capitalize">
                      {(taxSheetInfo?.projection_source || taxSummary?.ytd?.projection_source || 'Released Payroll').replace(/\s*\(.*?\)\s*/g, '').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {taxSummary?.ctc ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">CTC Breakdown</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="text-xs text-slate-500">Annual CTC</p><p className="text-xl font-bold text-slate-900">{taxSummary.ctc.ctc_annual?.toLocaleString()}</p></div>
                  <div><p className="text-xs text-slate-500">Monthly Gross</p><p className="text-xl font-bold text-emerald-600">{taxSummary.ctc.gross_monthly?.toLocaleString()}</p></div>
                  <div><p className="text-xs text-slate-500">Monthly Net</p><p className="text-xl font-bold text-[#0052FF]">{taxSummary.ctc.net_monthly?.toLocaleString()}</p></div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No CTC data available</p>
              </div>
            )}

            {/* Stage 73.20cm — YTD Tax Computation card. Surfaces the
                figures the payroll engine already computes per run
                (no extra processing step needed). */}
            {taxSummary?.ytd && (taxSummary.ytd.months_processed > 0 || taxSummary?.monthsProcessed > 0 || taxSheetInfo?.months_processed > 0 || taxSheetInfo?.months_paid > 0) ? (
              <div data-testid="ytd-tax-card" className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Tax Computation (YTD · FY {taxSummary.financial_year})
                  </h3>
                  <span className="text-xs text-slate-500">
                    {taxSummary.ytd.months_processed} month{taxSummary.ytd.months_processed === 1 ? '' : 's'} processed
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">Taxable Earnings (YTD)</p>
                    <p className="text-base font-bold text-slate-900">₹{taxSummary.ytd.taxable_earnings?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">Gross Earnings (YTD)</p>
                    <p className="text-base font-bold text-emerald-700">₹{taxSummary.ytd.gross_earnings?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">Net Paid (YTD)</p>
                    <p className="text-base font-bold text-[#0052FF]">₹{taxSummary.ytd.net_paid?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">TDS Deducted (YTD)</p>
                    <p className="text-base font-bold text-red-700">₹{taxSummary.ytd.tds_deducted?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">PF (YTD)</p>
                    <p className="text-base font-medium text-slate-800">₹{taxSummary.ytd.pf_deducted?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">ESI (YTD)</p>
                    <p className="text-base font-medium text-slate-800">₹{taxSummary.ytd.esi_deducted?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">Professional Tax (YTD)</p>
                    <p className="text-base font-medium text-slate-800">₹{taxSummary.ytd.professional_tax?.toLocaleString()}</p>
                  </div>
                  <div className="border border-slate-100 rounded-md p-3">
                    <p className="text-xs text-slate-500">LWF (YTD)</p>
                    <p className="text-base font-medium text-slate-800">₹{taxSummary.ytd.lwf_deducted?.toLocaleString()}</p>
                  </div>
                  <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                    <p className="text-xs text-amber-700">Projected Annual TDS</p>
                    <p className="text-base font-bold text-amber-800">₹{taxSummary.ytd.projected_annual_tds?.toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Figures are aggregated from finalized payroll runs (Apr → current month). Projected annual TDS is a linear forecast over the months already processed.
                </p>
              </div>
            ) : (
              <div data-testid="ytd-tax-empty" className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <TrendingUp className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Tax computation (YTD) will appear here once payroll has been processed for at least one month in this financial year.
                </p>
              </div>
            )}
            {taxSummary?.declaration ? (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Declaration Summary ({taxSummary.financial_year})</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Regime</span><span className="font-medium uppercase">{taxSummary.declaration.regime}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(taxSummary.declaration.status)}`}>{taxSummary.declaration.status}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Sections Declared</span><span className="font-medium">{taxSummary.declaration.sections?.length || 0}</span></div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-sm text-slate-400">No tax declarations submitted yet</p>
              </div>
            )}
            {taxSummary?.statutory_info && (
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Statutory Info</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-slate-500">UAN</p><p className="font-mono text-slate-700">{taxSummary.statutory_info.pf?.uan || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">PAN</p><p className="font-mono text-slate-700">{taxSummary.statutory_info.tds?.pan || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">ESIC</p><p className="font-mono text-slate-700">{taxSummary.statutory_info.esi?.esic_number || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Tax Regime</p><p className="font-medium uppercase">{taxSummary.statutory_info.tds?.regime || 'new'}</p></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* REIMBURSEMENTS TAB — View-Only Tracking Page */}
        {activeTab === 'reimbursement' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Total Claims</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{reimbursements.length}</p>
                <p className="text-[10px] text-slate-400 mt-1">Submitted claims count</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider">Pending Claims</p>
                <p className="text-xl font-bold text-amber-800 mt-1">{reimbursements.filter(r => r.status === 'pending').length}</p>
                <p className="text-[10px] text-amber-500 mt-1">Awaiting review</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Approved Claims</p>
                <p className="text-xl font-bold text-emerald-800 mt-1">{reimbursements.filter(r => r.status === 'approved' || r.status === 'paid').length}</p>
                <p className="text-[10px] text-emerald-500 mt-1">Approved for payout</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">Total Claimed Value</p>
                <p className="text-xl font-bold text-blue-800 mt-1">
                  {fmtINR(reimbursements.reduce((s, r) => s + (r.amount || 0), 0))}
                </p>
                <p className="text-[10px] text-blue-500 mt-1">Cumulative claimed value</p>
              </div>
            </div>

            {/* Search and Filters Bar */}
            <div className="flex flex-wrap items-center gap-3 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                <input 
                  type="text"
                  placeholder="Search Claim No / Expense Sheet Ref..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-xs pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                />
              </div>

              <div className="w-[150px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full text-xs px-2 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div className="w-[150px]">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full text-xs px-2 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                >
                  <option value="ALL">All Claim Types</option>
                  <option value="travel">Travel</option>
                  <option value="medical">Medical</option>
                  <option value="food">Food</option>
                  <option value="mobile">Mobile/Internet</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="w-[130px]">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                  title="From Date"
                />
              </div>

              <div className="w-[130px]">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                  title="To Date"
                />
              </div>

              <div className="flex gap-2 ml-auto">
                <button 
                  onClick={() => exportReimbursementsCSV()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 transition-colors"
                  title="Export to CSV"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 text-white rounded-lg text-xs hover:bg-slate-700 transition-colors"
                  title="Print List"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print</span>
                </button>
                {(searchTerm || statusFilter !== 'ALL' || categoryFilter !== 'ALL' || startDate || endDate) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('ALL');
                      setCategoryFilter('ALL');
                      setStartDate('');
                      setEndDate('');
                    }}
                    className="px-3 py-2 bg-slate-200 text-slate-750 hover:bg-slate-300 rounded-lg text-xs transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="text-xs text-slate-500">
                Track and submit expense reimbursement claims.
              </div>
              <button
                data-testid="new-reimbursement-btn"
                onClick={() => {
                  setReimbForm({
                    category: reimbPolicy?.items?.[0]?.category || 'travel',
                    amount: '',
                    description: '',
                    bill_date: new Date().toISOString().slice(0, 10),
                    bill_number: '',
                  });
                  setReimbFiles([]);
                  setShowReimbModal(true);
                }}
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span>New Claim</span>
              </button>
            </div>

            {/* Claims Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Claim No</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Expense Sheet Ref</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Employee</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Expense Type</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">Amount</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Approved Amount</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden sm:table-cell">Submitted Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">Payment Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredReimbursements.map((r, i) => (
                      <tr key={r.id || i} data-testid={`reimb-${i}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono font-medium text-slate-800">{r.claim_no || r.bill_number || '—'}</td>
                        <td className="px-4 py-3 font-mono text-blue-700 font-semibold">{r.expense_sheet_ref || r.claim_no || '—'}</td>
                        <td className="px-4 py-3 text-slate-700 hidden md:table-cell">{r.employee_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${categoryColor(r.category)}`}>
                            {(r.expense_type || r.category || '—').slice(0, 24)}{(r.expense_type || '').length > 24 ? '…' : ''}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtINR(r.amount)}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-700 hidden lg:table-cell">
                          {r.approved_amount != null ? fmtINR(r.approved_amount) : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">
                          {(r.submitted_date || r.created_at)?.slice(0, 10) || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${statusColor(r.status)}`}>
                            {r.status === 'approved' && !r.payment_date ? 'processing' : r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{r.payment_date || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              onClick={() => setSelectedReimbursement(r)}
                              className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline font-semibold"
                              title="View Details & Timeline"
                            >
                              <Eye className="w-3 h-3" /><span>View</span>
                            </button>
                            <button
                              onClick={() => handleReimbPdfDownload(r)}
                              className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline font-semibold"
                              title="Download PDF"
                            >
                              <Download className="w-3 h-3" /><span>PDF</span>
                            </button>
                            <button
                              onClick={() => handleReimbAttachmentsDownload(r)}
                              className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline font-semibold"
                              title="Download Attachments"
                            >
                              <Paperclip className="w-3 h-3" /><span>Files</span>
                            </button>
                            <button
                              onClick={() => window.print()}
                              className="flex items-center gap-0.5 text-[10px] text-slate-600 hover:underline font-medium"
                              title="Print"
                            >
                              <Printer className="w-3 h-3" /><span>Print</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredReimbursements.length === 0 && (
                <div className="py-16 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-slate-700">No reimbursement claims found</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Submit a claim via <span className="font-semibold">Expense Claim Sheets</span> to see it here.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* LOANS TAB */}
        {activeTab === 'loan' && (
          <div className="space-y-6">
            {/* Top Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Total Advances</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{fmtINR(ledgerSummary?.total_advance || 0)}</p>
                <p className="text-[10px] text-slate-400 mt-1">Overall requests approved</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider">Open Advances</p>
                <p className="text-xl font-bold text-amber-800 mt-1">{ledgerSummary?.total_open_advances || 0}</p>
                <p className="text-[10px] text-amber-500 mt-1">Currently active advances</p>
              </div>
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-rose-600 tracking-wider">Outstanding Position</p>
                <p className="text-xl font-bold text-rose-800 mt-1">{fmtINR(ledgerSummary?.current_outstanding_advance || 0)}</p>
                <p className="text-[10px] text-rose-500 mt-1">Pending adjustment/recovery</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Closed Advances</p>
                <p className="text-xl font-bold text-emerald-800 mt-1">{ledgerSummary?.total_closed_advances || 0}</p>
                <p className="text-[10px] text-emerald-500 mt-1">Fully settled advances</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Claims Submitted</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{ledgerSummary?.total_claim_submitted || 0}</p>
                <p className="text-[10px] text-slate-400 mt-1">Expense claims filed</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Claims Approved</p>
                <p className="text-xl font-bold text-emerald-800 mt-1">{ledgerSummary?.total_claim_approved || 0}</p>
                <p className="text-[10px] text-emerald-500 mt-1">Claims adjusted against adv.</p>
              </div>
              <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-sky-600 tracking-wider">Finance Paid</p>
                <p className="text-xl font-bold text-sky-800 mt-1">{fmtINR(ledgerSummary?.total_finance_paid || 0)}</p>
                <p className="text-[10px] text-sky-500 mt-1">Disbursed cash total</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider">Pending Settlement</p>
                <p className="text-xl font-bold text-amber-800 mt-1">{fmtINR(ledgerSummary?.net_balance || 0)}</p>
                <p className="text-[10px] text-amber-500 mt-1">Net pending balance</p>
              </div>
            </div>

            {/* Search and Filters Bar */}
            <div className="flex flex-wrap items-center gap-3 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                <input 
                  type="text"
                  placeholder="Search Advance Ref / Purpose / Reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-xs pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                />
              </div>

              <div className="w-[150px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full text-xs px-2 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="paid">Paid</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="w-[150px]">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full text-xs px-2 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                >
                  <option value="ALL">All Types</option>
                  <option value="salary_advance">Salary Advance</option>
                  <option value="festival_advance">Festival Advance</option>
                  <option value="travel_advance">Travel Advance</option>
                  <option value="medical_advance">Medical Advance</option>
                </select>
              </div>

              <div className="w-[130px]">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                  title="From Date"
                />
              </div>

              <div className="w-[130px]">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full text-xs px-2 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0052FF]"
                  title="To Date"
                />
              </div>

              <div className="flex gap-2 ml-auto">
                <button 
                  onClick={() => exportAdvancesCSV()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700 transition-colors"
                  title="Export to CSV (Excel)"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 text-white rounded-lg text-xs hover:bg-slate-700 transition-colors"
                  title="Print List"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print</span>
                </button>
                {(searchTerm || statusFilter !== 'ALL' || categoryFilter !== 'ALL' || startDate || endDate) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('ALL');
                      setCategoryFilter('ALL');
                      setStartDate('');
                      setEndDate('');
                    }}
                    className="px-3 py-2 bg-slate-200 text-slate-750 hover:bg-slate-300 rounded-lg text-xs transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button 
                data-testid="new-loan-btn" 
                onClick={() => setShowLoanModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC]"
              >
                <Plus className="w-4 h-4" />
                <span>New Advance Request</span>
              </button>
            </div>

            {/* Advance Cards List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAdvances.map((l, i) => {
                const adv = (ledgerSummary?.per_advance || []).find(a => a.advance_id === l.id);
                return (
                  <div 
                    key={i} 
                    data-testid={`loan-${i}`} 
                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start gap-2">
                        <span className="font-semibold text-slate-800 text-sm">
                          {l.loan_type?.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <div className="flex gap-1.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${statusColor(l.status)}`}>
                            {l.status}
                          </span>
                          {adv && (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                              adv.closed ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {adv.settlement_status || 'Open'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-slate-500">
                        <div>Advance ID: <span className="font-mono font-medium text-slate-700 truncate block">{l.id || 'Pending'}</span></div>
                        <div>Requested Date: <span className="font-medium text-slate-700">{l.created_at?.slice(0, 10) || '—'}</span></div>
                        <div className="col-span-2 mt-1">Purpose: <span className="font-medium text-slate-700 block">{l.reason || '—'}</span></div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs">
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold">Requested</span>
                          <span className="font-bold text-slate-800">{fmtINR(l.amount)}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold">Approved</span>
                          <span className="font-bold text-slate-800">{adv ? fmtINR(adv.approved_amount) : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold">Paid</span>
                          <span className="font-bold text-slate-800">{adv ? fmtINR(adv.finance_paid_amount) : '—'}</span>
                        </div>
                        <div>
                          <span className="text-[10px] text-slate-400 block font-bold text-rose-600">Outstanding</span>
                          <span className="font-bold text-rose-700">{adv ? fmtINR(adv.current_outstanding) : fmtINR(l.amount)}</span>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-500">
                        <div>EMI Recovery: <span className="font-medium text-slate-700">{fmtINR(l.emi_amount)} &times; {l.tenure_months}</span></div>
                        <div>Disbursed Date: <span className="font-medium text-slate-700">{adv?.finance_payment_date?.slice(0, 10) || '—'}</span></div>
                      </div>
                    </div>

                    <div className="border-t border-slate-100 mt-4 pt-3">
                      <div className="flex justify-end gap-3 text-[11px]">
                        <button 
                          onClick={() => setSelectedAdvance(l)}
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>View Details</span>
                        </button>
                        {l.status === 'approved' || l.status === 'paid' || l.status === 'closed' ? (
                          <button
                            data-testid={`loan-approval-pdf-${l.id}`}
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${API_URL}/payroll/loans/${l.id}/approval-form-pdf`, { withCredentials: true, responseType: 'blob' });
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement('a');
                                a.href = url; a.download = `loan_approval_${(l.id || '').slice(0, 8)}.pdf`; a.click();
                                URL.revokeObjectURL(url);
                              } catch (err) { console.error(err); }
                            }}
                            className="flex items-center gap-1 text-blue-600 hover:underline font-medium"
                          >
                            <Download className="w-3 h-3" />
                            <span>Download PDF</span>
                          </button>
                        ) : null}
                      </div>
                      {renderAdvanceTimeline(l, adv)}
                    </div>
                  </div>
                );
              })}
              {filteredAdvances.length === 0 && (
                <div className="col-span-full bg-slate-50 rounded-xl border border-slate-200 py-16 text-center">
                  <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-slate-700">No advance requests found</p>
                  <p className="text-xs text-slate-500 mt-1">Adjust search filters or request a new advance.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* REIMBURSEMENT MODAL */}
        {showReimbModal && (
          <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={() => setShowReimbModal(false)}>
            <div className="bg-white rounded-t-2xl md:rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="reimb-modal">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-900">Reimbursement Claim</h3>
                <button onClick={() => setShowReimbModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              {/* Policy summary header */}
              {reimbPolicy.has_policy ? (
                <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900 flex items-start gap-2" data-testid="reimb-policy-header">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Your designation policy</span>
                    {reimbPolicy.designation_name && <span> ({reimbPolicy.designation_name})</span>}
                    <span> defines {reimbPolicy.items.length} category cap{reimbPolicy.items.length === 1 ? '' : 's'}. Pick a category to see your limit.</span>
                  </div>
                </div>
              ) : (
                <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                  <div>No reimbursement policy defined for your designation — claim follows the standard approval flow.</div>
                </div>
              )}
              <form onSubmit={handleReimbSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    data-testid="reimb-category"
                    value={reimbForm.category}
                    onChange={(e) => setReimbForm({ ...reimbForm, category: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  >
                    {reimbPolicy.has_policy ? (
                      reimbPolicy.items.map((it) => (
                        <option key={it.category + (it.subcategory || '')} value={it.category}>{`${it.category}${it.subcategory ? ` — ${it.subcategory}` : ''}`}</option>
                      ))
                    ) : (
                      <>
                        <option value="travel">Travel</option>
                        <option value="medical">Medical</option>
                        <option value="food">Food</option>
                        <option value="mobile">Mobile</option>
                        <option value="other">Other</option>
                      </>
                    )}
                  </select>
                  {/* Inline cap badge — Stage 73.20p */}
                  {matchedPolicyItem && (
                    <div className="mt-2 text-[11px] text-slate-600" data-testid="reimb-cap-info">
                      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
                        <span className="font-semibold text-slate-700">Cap:</span>
                        <span className="font-mono">₹{Number(matchedPolicyItem.limit_amount).toLocaleString('en-IN')}</span>
                        <span className="text-slate-500">/ {matchedPolicyItem.limit_unit?.replace('per_', '')}</span>
                      </span>
                      {matchedPolicyItem.auto_approve_below != null && (
                        <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700">
                          Auto-approve under ₹{Number(matchedPolicyItem.auto_approve_below).toLocaleString('en-IN')}
                        </span>
                      )}
                      {matchedPolicyItem.requires_bill && (
                        <span className="ml-1.5 inline-flex items-center rounded bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-700">
                          Bill required
                        </span>
                      )}
                      {matchedPolicyItem.notes && (
                        <p className="mt-1 text-[10px] text-slate-500 italic">{matchedPolicyItem.notes}</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                  <input
                    type="number"
                    data-testid="reimb-amount"
                    value={reimbForm.amount}
                    onChange={(e) => setReimbForm({ ...reimbForm, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    required
                  />
                  {/* Live status pill */}
                  {liveCapStatus && liveCapStatus.amt > 0 && (
                    <div className="mt-1.5 text-xs" data-testid="reimb-cap-status">
                      {liveCapStatus.autoApprove ? (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 border border-emerald-200 text-emerald-800 px-2 py-1">
                          <CheckCircle className="w-3 h-3" />
                          Within cap & under auto-approve threshold — will be approved instantly.
                        </span>
                      ) : liveCapStatus.within ? (
                        <span className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1">
                          <CheckCircle className="w-3 h-3" />
                          Within cap — standard approval flow.
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1" data-testid="reimb-cap-exceeds">
                          <AlertTriangle className="w-3 h-3" />
                          Exceeds cap by ₹{liveCapStatus.exceedsBy.toLocaleString('en-IN')} — will route to <b className="ml-0.5">SPECIAL approval</b>.
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><textarea data-testid="reimb-desc" value={reimbForm.description} onChange={(e) => setReimbForm({ ...reimbForm, description: e.target.value })} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Bill Date</label><input type="date" value={reimbForm.bill_date} onChange={(e) => setReimbForm({ ...reimbForm, bill_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Bill #</label><input value={reimbForm.bill_number} onChange={(e) => setReimbForm({ ...reimbForm, bill_number: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" /></div>
                </div>

                {/* Stage 73.20dd — Bill attachments (image / PDF, ≤10MB each) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Bill Attachments (Image / PDF)</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    data-testid="reimb-bill-files"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      const oversize = picked.filter(f => f.size > 10 * 1024 * 1024);
                      if (oversize.length) {
                        toast.error(`${oversize.map(f => f.name).join(', ')} exceeds 10 MB limit — skipped`);
                      }
                      setReimbFiles([...reimbFiles, ...picked.filter(f => f.size <= 10 * 1024 * 1024)]);
                      e.target.value = ''; // allow re-picking same file
                    }}
                    className="w-full text-xs border border-dashed border-slate-300 rounded-lg px-3 py-2 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-slate-100 hover:file:bg-slate-200 file:font-medium"
                  />
                  {reimbFiles.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid="reimb-file-list">
                      {reimbFiles.map((f, i) => (
                        <li key={i} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span className="truncate">{f.name}</span>
                            <span className="text-slate-400 shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
                          </span>
                          <button
                            type="button"
                            data-testid={`remove-file-${i}`}
                            onClick={() => setReimbFiles(reimbFiles.filter((_, j) => j !== i))}
                            className="text-red-500 hover:text-red-700 shrink-0 ml-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-1 text-[10px] text-slate-500">Attach receipts / invoices — image (JPG/PNG/HEIC/WEBP) or PDF, max 10 MB per file.</p>
                </div>

                <button
                  data-testid="submit-reimb-btn"
                  type="submit"
                  disabled={submittingReimb}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submittingReimb ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>Submit Claim</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* LOAN MODAL */}
        {showLoanModal && (
          <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={() => setShowLoanModal(false)}>
            <div className="bg-white rounded-t-2xl md:rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="loan-modal">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-900">Advance Request</h3>
                <button onClick={() => setShowLoanModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              {/* Policy summary header */}
              {loanPolicy.has_policy ? (
                <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900 flex items-start gap-2" data-testid="loan-policy-header">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Your designation policy</span>
                    {loanPolicy.designation_name && <span> ({loanPolicy.designation_name})</span>}
                    <span> allows {loanPolicy.items.length} loan type{loanPolicy.items.length === 1 ? '' : 's'}. Pick a type to see your principal &amp; tenure caps.</span>
                  </div>
                </div>
              ) : (
                <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                  <div>No advance policy defined for your designation — request follows standard approval flow.</div>
                </div>
              )}
              <form onSubmit={handleLoanSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Advance Type</label>
                  <select
                    data-testid="loan-type"
                    value={loanForm.loan_type}
                    onChange={(e) => setLoanForm({ ...loanForm, loan_type: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  >
                    {loanPolicy.has_policy ? (
                      loanPolicy.items.map((it) => (
                        <option key={it.loan_type} value={it.loan_type}>
                          {it.label || it.loan_type.replace(/_/g, ' ')}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="salary_advance">Salary Advance</option>
                        <option value="personal_loan">Personal Loan</option>
                        <option value="emergency_advance">Emergency Loan</option>
                      </>
                    )}
                  </select>
                  {/* Inline caps */}
                  {matchedLoanPolicyItem && (
                    <div className="mt-2 text-[11px] text-slate-600" data-testid="loan-cap-info">
                      <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
                        <span className="font-semibold text-slate-700">Max Principal:</span>
                        <span className="font-mono">₹{Number(matchedLoanPolicyItem.max_principal).toLocaleString('en-IN')}</span>
                      </span>
                      <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
                        <span className="font-semibold text-slate-700">Tenure:</span>
                        <span className="font-mono">{matchedLoanPolicyItem.min_tenure_months}–{matchedLoanPolicyItem.max_tenure_months} mo</span>
                      </span>
                      {matchedLoanPolicyItem.interest_rate_default > 0 && (
                        <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700">
                          Default rate {matchedLoanPolicyItem.interest_rate_default}%
                        </span>
                      )}
                      {matchedLoanPolicyItem.notes && (
                        <p className="mt-1 text-[10px] text-slate-500 italic">{matchedLoanPolicyItem.notes}</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                  <input
                    type="number"
                    data-testid="loan-amount"
                    value={loanForm.amount}
                    onChange={(e) => setLoanForm({ ...loanForm, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tenure (Months)</label>
                  <input
                    type="number"
                    data-testid="loan-tenure"
                    value={loanForm.tenure_months}
                    onChange={(e) => setLoanForm({ ...loanForm, tenure_months: parseInt(e.target.value) || 1 })}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                {/* Live policy status */}
                {liveLoanStatus && (
                  <div className="text-xs space-y-1" data-testid="loan-policy-status">
                    {!liveLoanStatus.allowed ? (
                      <span className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1">
                        <AlertTriangle className="w-3 h-3" />
                        This loan type is NOT in your designation policy — will route to <b className="ml-0.5">SPECIAL approval</b>.
                      </span>
                    ) : liveLoanStatus.principalWithin && liveLoanStatus.tenureWithin && loanForm.amount > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded bg-emerald-100 border border-emerald-200 text-emerald-800 px-2 py-1">
                        <CheckCircle className="w-3 h-3" />
                        Within all caps — standard approval flow.
                      </span>
                    ) : (
                      <div className="space-y-1">
                        {!liveLoanStatus.principalWithin && loanForm.amount > 0 && (
                          <span className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1" data-testid="loan-principal-exceeds">
                            <AlertTriangle className="w-3 h-3" />
                            Exceeds principal cap by ₹{liveLoanStatus.principalExceedsBy.toLocaleString('en-IN')} — SPECIAL approval.
                          </span>
                        )}
                        {!liveLoanStatus.tenureWithin && loanForm.tenure_months > 0 && (
                          <span className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-2 py-1" data-testid="loan-tenure-exceeds">
                            <AlertTriangle className="w-3 h-3" />
                            {liveLoanStatus.tenureUnderMin ? (
                              <>Tenure under min ({liveLoanStatus.minTenure} mo) — SPECIAL approval.</>
                            ) : (
                              <>Exceeds tenure cap by {liveLoanStatus.tenureExceedsBy} months — SPECIAL approval.</>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reason</label><textarea value={loanForm.reason} onChange={(e) => setLoanForm({ ...loanForm, reason: e.target.value })} rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" /></div>
                {loanForm.amount > 0 && loanForm.tenure_months > 0 && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">Estimated EMI: <span className="font-bold">{Math.round(loanForm.amount / loanForm.tenure_months).toLocaleString()}</span>/month</p>}
                <button data-testid="submit-loan-btn" type="submit" className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC]">Submit Request</button>
              </form>
            </div>
          </div>
        )}
        {/* ADVANCE DETAILS MODAL */}
        {selectedAdvance && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setSelectedAdvance(null)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl relative" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-3 mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-blue-600" />
                  Advance Request Details
                </h3>
                <button onClick={() => setSelectedAdvance(null)} className="p-1 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-xs font-medium">
                <div>
                  <span className="text-slate-400 block">Advance ID:</span>
                  <span className="font-mono font-bold text-slate-800">{selectedAdvance.id || 'Pending'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Advance Type:</span>
                  <span className="font-semibold text-slate-800">{selectedAdvance.loan_type?.replace(/_/g, ' ').toUpperCase()}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Request Date:</span>
                  <span className="font-semibold text-slate-800">{selectedAdvance.created_at?.slice(0, 10) || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Purpose:</span>
                  <span className="font-semibold text-slate-800">{selectedAdvance.reason || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Requested Amount:</span>
                  <span className="font-bold text-slate-800">{fmtINR(selectedAdvance.amount)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">EMI Scheme:</span>
                  <span className="font-semibold text-slate-800">{fmtINR(selectedAdvance.emi_amount)} &times; {selectedAdvance.tenure_months} Months</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Outstanding Balance:</span>
                  <span className="font-bold text-amber-700">
                    {(() => {
                      const adv = (ledgerSummary?.per_advance || []).find(a => a.advance_id === selectedAdvance.id);
                      return fmtINR(adv?.current_outstanding ?? selectedAdvance.amount);
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block">Settlement Status:</span>
                  <span className="font-semibold text-slate-800">
                    {(() => {
                      const adv = (ledgerSummary?.per_advance || []).find(a => a.advance_id === selectedAdvance.id);
                      return adv?.settlement_status || 'Pending';
                    })()}
                  </span>
                </div>
              </div>

              {selectedAdvance.policy_check?.needs_deviation_approval && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
                  <div>
                    <p className="font-semibold">Policy Deviation Tagged</p>
                    <p className="text-[10px] mt-0.5">{selectedAdvance.policy_check.policy_note || 'This request exceeds standard designation caps.'}</p>
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <button 
                  onClick={() => setSelectedAdvance(null)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REIMBURSEMENT DETAILS & TIMELINE MODAL */}
        {selectedReimbursement && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReimbursement(null)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-3 mb-4">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  Reimbursement Claim Details
                </h3>
                <button onClick={() => setSelectedReimbursement(null)} className="p-1 hover:bg-slate-100 rounded-full">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-xs font-medium">
                <div>
                  <span className="text-slate-400 block">Claim No:</span>
                  <span className="font-mono font-bold text-slate-800">{selectedReimbursement.claim_no || selectedReimbursement.bill_number || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Expense Sheet Ref:</span>
                  <span className="font-mono text-blue-700 font-bold">{selectedReimbursement.expense_sheet_ref || selectedReimbursement.title || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Employee:</span>
                  <span className="text-slate-800 font-semibold">{selectedReimbursement.employee_name || '—'} {selectedReimbursement.department ? `(${selectedReimbursement.department})` : ''}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Expense Type:</span>
                  <span className="text-slate-800 font-semibold">{selectedReimbursement.expense_type || selectedReimbursement.category?.toUpperCase() || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Claim Amount:</span>
                  <span className="font-bold text-slate-800">{fmtINR(selectedReimbursement.amount)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Approved Amount:</span>
                  <span className="font-bold text-emerald-700">{selectedReimbursement.approved_amount != null ? fmtINR(selectedReimbursement.approved_amount) : '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Submitted Date:</span>
                  <span className="text-slate-800">{selectedReimbursement.submitted_date?.slice(0, 10) || selectedReimbursement.created_at?.slice(0, 10) || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Current Status:</span>
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mt-1 uppercase ${statusColor(selectedReimbursement.status)}`}>
                    {selectedReimbursement.status}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block">Payment Date:</span>
                  <span className="text-slate-800 font-semibold">{selectedReimbursement.payment_date || '—'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Payment Ref / Bank UTR:</span>
                  <span className="font-mono text-slate-800 font-semibold">{selectedReimbursement.payment_reference || selectedReimbursement.bank_transaction_number || '—'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-400 block">Description / Sheet Title:</span>
                  <span className="text-slate-800 block p-2 bg-slate-50 border rounded mt-1 font-normal">{selectedReimbursement.title || selectedReimbursement.description || '—'}</span>
                </div>
              </div>

              {/* Approval Workflow Timeline */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <p className="text-xs font-bold text-slate-800 mb-2">Approval &amp; Payment Workflow</p>
                {renderReimbursementTimeline(selectedReimbursement)}
              </div>

              {/* Actions & File Download */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleReimbPdfDownload(selectedReimbursement)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download PDF</span>
                  </button>
                  <button 
                    onClick={() => handleReimbAttachmentsDownload(selectedReimbursement)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-100 transition-colors"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    <span>Download Attachments ({selectedReimbursement.attachment_count || 0})</span>
                  </button>
                </div>
                <button 
                  onClick={() => setSelectedReimbursement(null)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs hover:bg-slate-50 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ESSPayTax;
